import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Plus, Trash2 } from "lucide-react";

// ── Types ────────────────────────────────────────────────────

interface ShadowLayer {
    id: number;
    x: number;
    y: number;
    blur: number;
    spread: number;
    color: string;
    opacity: number;
    inset: boolean;
}

function createLayer(id: number): ShadowLayer {
    return { id, x: 4, y: 4, blur: 16, spread: 0, color: "#000000", opacity: 25, inset: false };
}

function layerToCSS(l: ShadowLayer): string {
    const r = parseInt(l.color.slice(1, 3), 16);
    const g = parseInt(l.color.slice(3, 5), 16);
    const b = parseInt(l.color.slice(5, 7), 16);
    return `${l.inset ? "inset " : ""}${l.x}px ${l.y}px ${l.blur}px ${l.spread}px rgba(${r}, ${g}, ${b}, ${(l.opacity / 100).toFixed(2)})`;
}

// ── Component ────────────────────────────────────────────────

export function BoxShadowGenerator() {
    const [layers, setLayers] = useState<ShadowLayer[]>([createLayer(1)]);
    const [copied, setCopied] = useState(false);
    const [nextId, setNextId] = useState(2);
    const [previewBg, setPreviewBg] = useState("#18181b");

    useEffect(() => {
        document.title = "CSS Box Shadow Generator — Visual Shadow Builder — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free CSS box-shadow generator. Visual controls for offset, blur, spread, color, and opacity. Multiple shadow layers. Copy CSS instantly.",
            );
        }
    }, []);

    const cssValue = layers.map(layerToCSS).join(",\n    ");
    const cssOutput = `box-shadow: ${cssValue};`;

    const updateLayer = (id: number, updates: Partial<ShadowLayer>) => {
        setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...updates } : l)));
    };

    const addLayer = () => {
        setLayers((prev) => [...prev, createLayer(nextId)]);
        setNextId((n) => n + 1);
    };

    const removeLayer = (id: number) => {
        if (layers.length <= 1) return;
        setLayers((prev) => prev.filter((l) => l.id !== id));
    };

    const copy = async () => {
        await navigator.clipboard.writeText(cssOutput);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Box Shadow Generator</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Box Shadow Generator</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Build CSS box shadows visually. Adjust offset, blur, spread, color, and opacity with sliders. Add multiple layers.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Preview */}
                    <div>
                        <div className="flex items-center justify-between text-[11px] text-zinc-600 font-mono uppercase tracking-wider mb-3">
                            <span>Preview</span>
                            <div className="flex items-center gap-2">
                                <span className="normal-case tracking-normal">bg</span>
                                <input
                                    type="color"
                                    value={previewBg}
                                    onChange={(e) => setPreviewBg(e.target.value)}
                                    className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                                />
                            </div>
                        </div>
                        <div
                            className="w-full aspect-square rounded-2xl border border-white/[0.06] flex items-center justify-center"
                            style={{ backgroundColor: previewBg }}
                        >
                            <div
                                className="w-48 h-48 bg-[#27272a] rounded-2xl"
                                style={{ boxShadow: layers.map(layerToCSS).join(", ") }}
                            />
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="space-y-4">
                        {layers.map((layer, idx) => (
                            <div key={layer.id} className="bg-[#111113] border border-white/[0.06] rounded-xl p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                        Layer {idx + 1}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={layer.inset}
                                                onChange={(e) => updateLayer(layer.id, { inset: e.target.checked })}
                                                className="rounded border-white/[0.1] bg-transparent"
                                            />
                                            Inset
                                        </label>
                                        {layers.length > 1 && (
                                            <button onClick={() => removeLayer(layer.id)} className="text-zinc-700 hover:text-red-400 transition-colors">
                                                <Trash2 size={12} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    {([
                                        { key: "x", label: "Offset X", min: -50, max: 50 },
                                        { key: "y", label: "Offset Y", min: -50, max: 50 },
                                        { key: "blur", label: "Blur", min: 0, max: 100 },
                                        { key: "spread", label: "Spread", min: -50, max: 50 },
                                    ] as const).map(({ key, label, min, max }) => (
                                        <div key={key}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-[10px] text-zinc-600">{label}</span>
                                                <span className="text-[10px] text-zinc-500 font-mono">{layer[key]}px</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={min}
                                                max={max}
                                                value={layer[key]}
                                                onChange={(e) => updateLayer(layer.id, { [key]: parseInt(e.target.value) })}
                                                className="w-full accent-violet-400"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-3">
                                    <div>
                                        <span className="text-[10px] text-zinc-600 block mb-1">Color</span>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={layer.color}
                                                onChange={(e) => updateLayer(layer.id, { color: e.target.value })}
                                                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                                            />
                                            <span className="text-[11px] text-zinc-400 font-mono">{layer.color}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[10px] text-zinc-600">Opacity</span>
                                            <span className="text-[10px] text-zinc-500 font-mono">{layer.opacity}%</span>
                                        </div>
                                        <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            value={layer.opacity}
                                            onChange={(e) => updateLayer(layer.id, { opacity: parseInt(e.target.value) })}
                                            className="w-full accent-violet-400"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={addLayer}
                            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 transition-colors w-full justify-center"
                        >
                            <Plus size={12} /> Add shadow layer
                        </button>
                    </div>
                </div>

                {/* CSS output */}
                <div className="mb-16">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                        <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">CSS Output</span>
                        <button onClick={copy} className="flex items-center gap-1.5 text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors">
                            {copied ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    <div className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4">
                        <pre className="text-[13px] text-zinc-300 font-mono whitespace-pre-wrap">{cssOutput}</pre>
                    </div>
                </div>

                {/* SEO */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About CSS box-shadow</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>The CSS box-shadow property adds shadow effects around an element's frame. You can set the shadow's color, size, blur radius, and position. Multiple shadows can be layered for complex effects like elevation, glow, or neumorphism.</p>
                        <p>The syntax is: box-shadow: [inset] offset-x offset-y blur-radius spread-radius color. This generator lets you build shadows visually and copy the CSS output directly.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/color-converter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Color Converter</Link>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                        <Link to="/tools/diff-checker" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Diff Checker</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
